import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { useId, useState, type FC, type FormEvent, type ChangeEvent } from "react";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { useCreateProjectFromEnvironment } from "@/queries/project-queries";
import { ExternalLink } from "@/components/podkit/typography/Link";

export const ConversationMessageCreateProject: FC<{
    environment: PlainEnvironment;
    hasAgentExecution: boolean;
}> = ({ environment, hasAgentExecution }) => {
    const { toast } = useToast();
    const createProjectFromEnvironment = useCreateProjectFromEnvironment();

    const [projectName, setProjectName] = useState<string>();
    const inputNameID = useId();

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!projectName) {
            return;
        }
        await createProjectFromEnvironment.mutateAsync(
            {
                projectName,
                environmentId: environment.id,
            },
            {
                onSuccess: () => {
                    toast({
                        title: "Project created from environment",
                    });
                },
                onError: (error: unknown) => {
                    toast({
                        title: "Failed to create project from environment",
                        description: error instanceof Error ? formatError(error) : String(error),
                    });
                },
            },
        );
    };

    const handleProjectNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setProjectName(e.target.value.trimStart().replace(/\s+/g, " "));
    };

    return (
        <div data-testid="conversation-message-create-project" className="contents">
            {hasAgentExecution ? (
                <Text className="text-base">
                    Now that everything has been configured and pushed, we can use this environment as a blueprint for
                    new environments in the future by creating a{" "}
                    <ExternalLink href="https://www.gitpod.io/docs/gitpod/projects/overview">Project</ExternalLink>.
                </Text>
            ) : (
                <Text className="text-base">
                    It looks like this repository is already configured. Do you want to create a{" "}
                    <ExternalLink href="https://www.gitpod.io/docs/gitpod/projects/overview">Project</ExternalLink>?
                </Text>
            )}
            <Text className="text-base">
                Projects tie together repositories, runners, and environments, which enables team members to create
                fully configured environments with just one click. Once created, you can always modify the project to
                update its settings later.
            </Text>
            <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                <InputField label="Project name" id={inputNameID}>
                    <Input
                        id={inputNameID}
                        type="text"
                        value={projectName || ""}
                        placeholder="Ex: My Project"
                        onChange={handleProjectNameChange}
                    />
                </InputField>
                <div className="flex flex-wrap gap-2">
                    <Button
                        type="submit"
                        variant="primary"
                        loading={createProjectFromEnvironment.isPending}
                        disabled={!projectName}
                    >
                        Create Project
                    </Button>
                </div>
            </form>
        </div>
    );
};
